import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { PATHS } from '@belong/common';
import { IconClose } from '@belong/icons';
import { Text, Image, ButtonBase } from '@belong/ui';
import type { MaintenanceRecurrenceModel } from 'api/models';
import clsx from 'clsx';
import { DropdownFinalFormAdapter } from 'components/Dropdown/Dropdown';
import Field from 'components/Field/Field';
import Form from 'components/Form/Form';
import Grid from 'components/Grid/Grid';
import { Loading } from 'components/Loading/Loading';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { URL_QUERY_PARAMS } from 'maintenance/constants';
import { useMaintenanceHomes } from 'maintenance/hooks/useMaintenanceHomes';
import { STRINGS } from 'maintenance/strings';
import {
  recurringTasksHomeowner,
  residentTasks,
  getRecurringTasksByUnit,
  Task,
  baseTasks,
} from 'maintenance/utils';
import RenterAccountLease from 'models/renterAccounts/RenterAccountLease';
import { fetchRecurringTasks } from 'store/redux/maintenance/actions';
import { selectRecurringTasks } from 'store/redux/maintenance/selectors';
import { fetchRecurringProServicePresets } from 'store/redux/pros/actions';
import { fetchResidentLeases } from 'store/redux/renter-accounts/actions';
import { selectLeases } from 'store/redux/renter-accounts/selectors';
import {
  getLeaseStatus,
  LEASE_STATUS,
} from 'utils/formateDate/getTimeForLeasingStatus';
import css from './add-task.module.css';

type MaintenanceRecurrentSubtaskProps = {
  history: RouteComponentProps['history'];
  isHomeowner?: boolean;
  isMaintenanceAgentEnabled?: boolean;
};

type MaintenanceCreateFormValues = {
  home: string;
};

export const AddTask = ({
  history,
  isHomeowner,
  isMaintenanceAgentEnabled = false,
}: MaintenanceRecurrentSubtaskProps) => {
  const dispatch = useDispatch();
  const { homes, hasSingleHomeFamily } = useMaintenanceHomes();
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<Task | null>(null);
  const [selectedHome, setSelectedHome] = useState<string | null>(null);
  const recurringTasks = useSelector<unknown, MaintenanceRecurrenceModel[]>(
    selectRecurringTasks,
  );
  const leases = useSelector<unknown, RenterAccountLease[]>(selectLeases);
  const formFooterRef = useRef<HTMLDivElement | null>(null);

  const recurringTasksByUnit = getRecurringTasksByUnit(recurringTasks);
  const tasks = isHomeowner ? recurringTasksHomeowner : residentTasks;
  const activeTasks = selectedHome ? recurringTasksByUnit[selectedHome] : [];
  const currentLease = leases?.filter(
    (lease) => getLeaseStatus(lease) === LEASE_STATUS.CURRENT,
  )?.[0];
  const formInitialValues = isHomeowner ? { home: homes[0]?.key } : {};

  useEffect(() => {
    async function fetchRecurring() {
      setIsLoadingTasks(true);
      await dispatch(fetchRecurringTasks());
      await dispatch(fetchRecurringProServicePresets());
      if (!isHomeowner) {
        await dispatch(fetchResidentLeases());
      }
      setIsLoadingTasks(false);
    }

    fetchRecurring();
  }, [dispatch, isHomeowner]);

  useEffect(() => {
    if (!selectedHome) {
      if (isHomeowner && homes.length > 0) {
        setSelectedHome(homes[0]!.key);
      }
      if (!isHomeowner && !!currentLease) {
        setSelectedHome(currentLease.unitInfo.unitId);
      }
    }
  }, [currentLease, homes, isHomeowner, selectedHome]);

  const navigateBack = () => {
    history.push(
      isHomeowner
        ? PATHS.HOMEOWNER_ACCOUNT_MAINTENANCE
        : PATHS.RESIDENTS_ACCOUNT_MAINTENANCE,
    );
  };

  const handleSubmit = (values: MaintenanceCreateFormValues) => {
    // The submit button is `disabled` unless `selectedPreset` is set (see the
    // `disabled` prop below), so it is never null by the time this runs.
    const preset = selectedPreset!;

    // TODO: Maintenance agent only for resident
    if (isMaintenanceAgentEnabled && !preset.recurring && !isHomeowner) {
      const isRepair = preset.key === 'Repair';
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append('type', isRepair ? 'repair' : 'upgrade');
      urlSearchParams.append('from', 'create');

      return history.replace(
        `${PATHS.RESIDENT_MAINTENANCE_SCHEDULE.replace(
          ':unitId',
          currentLease?.unitInfo.unitId,
        )}?${urlSearchParams.toString()}`,
      );
    }

    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append(URL_QUERY_PARAMS.UNIT_ID, values.home);

    if (isHomeowner) {
      return history.push(`${preset.key}?${urlSearchParams.toString()}`);
    }

    return history.push(`${preset.key}`);
  };

  const onServiceClick = (task: Task) => {
    setSelectedPreset(task);
    formFooterRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  if (isLoadingTasks) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-end py-xs">
        <ButtonBase className="p-xs sm:p-sm" onClick={navigateBack}>
          <IconClose height={16} />
        </ButtonBase>
      </div>
      <Grid className={clsx('md:p-sm', css.grid)} extraPadding>
        <Text variant="h2" fontWeight="semibold" className="mb-2xl text-center">
          {STRINGS.addTitle}
        </Text>
        <Form
          initialValues={formInitialValues}
          onSubmit={handleSubmit}
          getForm={() => {
            return (
              <div>
                {isHomeowner && homes.length > 1 && (
                  <div className="mb-2xl">
                    <Text variant="h3" fontWeight="semibold" className="mb-xs">
                      Which Home?
                    </Text>
                    <Field
                      component={DropdownFinalFormAdapter}
                      items={homes}
                      name="home"
                      disabled={homes?.length === 1}
                      placeholder="Home"
                      withMarginTop={false}
                      onChangeCustom={setSelectedHome}
                    />
                  </div>
                )}

                <Text variant="h3" fontWeight="semibold" className="mb-md">
                  {'What can our Pros do for you\u00a0today?'}
                </Text>
                <div className="mb-2xl grid grid-cols-2 gap-sm">
                  {baseTasks.map((task) => {
                    if (
                      isHomeowner &&
                      !hasSingleHomeFamily &&
                      !task.multiUnit
                    ) {
                      return;
                    }

                    return (
                      <ServiceItem
                        task={task}
                        key={task.key}
                        isSelected={selectedPreset?.key === task.key}
                        onServiceClick={() => onServiceClick(task)}
                      />
                    );
                  })}
                </div>

                <Text variant="h3" fontWeight="semibold" className="mb-md">
                  Recurring Services Available
                </Text>
                <div className="mb-2xl grid grid-cols-2 gap-sm md:grid-cols-3 md:gap-md lg:grid-cols-4">
                  {tasks.map((task) => {
                    if (
                      isHomeowner &&
                      !hasSingleHomeFamily &&
                      !task.multiUnit
                    ) {
                      return;
                    }
                    const isRecurringEnabled = task.recurring
                      ? !!activeTasks?.find(
                          (homeTask) =>
                            homeTask.recurringTask === task.key &&
                            !homeTask.isCancelled,
                        )
                      : false;

                    return (
                      <ServiceItem
                        task={task}
                        key={task.key}
                        isSelected={selectedPreset?.key === task.key}
                        isEnabled={isRecurringEnabled}
                        onServiceClick={() => onServiceClick(task)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          }}
          getFormBottomBar={(
            _form: never,
            nextButtonProps: Record<string, unknown>,
          ) => (
            <div
              className="mb-3xl flex w-full items-center justify-center"
              ref={formFooterRef}
            >
              <FormLayout.Button
                buttonClassNames="w-button-fluid sm:w-button-default h-button"
                ctaProps={{
                  label: 'Next',
                  disabled: isHomeowner
                    ? !selectedHome || !selectedPreset
                    : !selectedPreset,
                }}
                nextButtonWrapperProps={nextButtonProps}
              />
            </div>
          )}
          id="pro-service-create"
          allowSubmitErrors={false}
          customError=""
        />
      </Grid>
    </>
  );
};

const ServiceItem = ({
  task,
  isSelected,
  isEnabled = false,
  onServiceClick,
}: {
  task: Task;
  isSelected: boolean;
  isEnabled?: boolean;
  onServiceClick: () => void;
}) => {
  return (
    <div
      className={clsx(
        'group relative flex cursor-pointer flex-col overflow-hidden rounded shadow-md transition-all duration-hover hover:bg-navy hover:text-white',
        isSelected ? 'bg-navy text-white' : 'bg-white text-navy',
      )}
      onClick={onServiceClick}
    >
      <div
        className={clsx(
          'absolute inset-x-0 top-0 flex items-center justify-end gap-xs p-xs',
          css.overlay,
        )}
      >
        {task.recurring && (
          <>
            {isEnabled && (
              <div
                className={clsx(
                  'z-10 flex items-center justify-center rounded border-2 border-solid border-green bg-light-green bg-opacity-60',
                  css.pill,
                )}
              >
                <span className="px-sm text-p1 font-semibold leading-p2 text-white">
                  {STRINGS.on}
                </span>
              </div>
            )}
          </>
        )}
      </div>
      <div className={clsx('overflow-hidden', css.image)}>
        <Image className={css.image} src={task.image} alt={task.label} />
      </div>
      <div className="flex min-h-header flex-1 items-center justify-center px-xs py-sm md:py-xs">
        <Text
          className={clsx(
            'whitespace-pre-wrap text-center duration-hover group-hover:font-semibold',
            isSelected ? 'font-semibold' : 'font-regular',
          )}
        >
          {task.label}
        </Text>
      </div>
    </div>
  );
};
